import React, { useEffect, useState, useRef } from "react";
import { X, Sparkles, Clock, ShieldCheck, Award, ChevronRight, GraduationCap } from "lucide-react";

const STORAGE_KEY = "acdt_exit_popup_dismissed_at";
const COOLDOWN_HOURS = 24;
const IDLE_MS = 3000; // non prima di 3s dall'ingresso

function track(event, props = {}) {
  try {
    window.posthog?.capture?.(event, { experiment: "exit_intent_v1", ...props });
  } catch { /* posthog non disponibile */ }
}

/**
 * Exit-Intent Popup: si attiva quando il mouse esce dalla parte alta del viewport
 * (indicando intento di chiudere/cambiare tab). Mobile fallback: alla prima
 * inversione di scroll rapido verso l'alto.
 * Cooldown 24h via localStorage, mostrato una sola volta per sessione.
 */
export default function ExitIntentPopup() {
  const [show, setShow] = useState(false);
  const shownRef = useRef(false);
  const readyRef = useRef(false);
  const shownAtRef = useRef(0);
  const lastScrollY = useRef(0);
  const lastScrollT = useRef(0);

  useEffect(() => {
    // Skip se già chiuso di recente
    try {
      const ts = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
      if (ts && Date.now() - ts < COOLDOWN_HOURS * 3600 * 1000) return;
    } catch { /* localStorage non disponibile */ }

    const ready = setTimeout(() => { readyRef.current = true; }, IDLE_MS);

    const trigger = (motivo) => {
      if (shownRef.current || !readyRef.current) return;
      shownRef.current = true;
      shownAtRef.current = Date.now();
      setShow(true);
      track("exit_intent_shown", { motivo });
    };

    // Desktop: mouse esce dal top del viewport
    const onMouseOut = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget && !e.toElement) trigger("mouse_top");
    };

    // Mobile fallback: scroll molto rapido verso l'alto dopo aver scrollato in basso
    const onScroll = () => {
      const y = window.scrollY;
      const t = Date.now();
      const dy = y - lastScrollY.current;
      const dt = t - lastScrollT.current;
      if (y < 200 && dy < -300 && dt < 500) trigger("scroll_up_rapido");
      lastScrollY.current = y;
      lastScrollT.current = t;
    };

    document.addEventListener("mouseout", onMouseOut);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      clearTimeout(ready);
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const dismiss = (motivo = "close") => {
    const secondi = shownAtRef.current
      ? Math.round((Date.now() - shownAtRef.current) / 1000)
      : 0;
    track("exit_intent_dismissed", { motivo, secondi_visti: secondi });
    setShow(false);
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* ignore */ }
  };

  const handleCta = () => {
    const secondi = shownAtRef.current
      ? Math.round((Date.now() - shownAtRef.current) / 1000)
      : 0;
    track("exit_intent_cta_click", { secondi_visti: secondi });
    setShow(false);
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* ignore */ }
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-[fadeIn_0.3s_ease-out]"
      data-testid="exit-intent-popup"
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-popup-title"
    >
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes popIn {
          from { transform: scale(0.9) translateY(20px); opacity: 0 }
          to { transform: scale(1) translateY(0); opacity: 1 }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0 }
          100% { background-position: 200% 0 }
        }
        .exit-shimmer {
          background: linear-gradient(90deg, #f59e0b 0%, #fbbf24 50%, #f59e0b 100%);
          background-size: 200% 100%;
          animation: shimmer 3s linear infinite;
        }
        .exit-pop { animation: popIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); }
      `}</style>

      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => dismiss("backdrop")}
        aria-hidden="true"
      />

      {/* Card */}
      <div className="exit-pop relative w-full max-w-lg rounded-3xl overflow-hidden bg-white shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5)] border-2 border-[#f59e0b]/40">
        <button
          onClick={() => dismiss("x_button")}
          aria-label="Chiudi"
          data-testid="exit-popup-close"
          className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/90 hover:bg-white text-slate-500 hover:text-slate-800 flex items-center justify-center z-20 transition-colors shadow-md"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Header shimmer */}
        <div className="exit-shimmer px-6 py-5 text-[#0a1020]">
          <div className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.2em]">
            <Sparkles className="h-4 w-4" />
            Aspetta! Un&apos;offerta solo per te
          </div>
          <h2
            id="exit-popup-title"
            className="mt-2 text-[22px] md:text-[26px] font-extrabold leading-tight"
          >
            Ottieni il Corso A.C.D.T. a <span className="underline decoration-white/40">€ 99</span>
          </h2>
          <p className="mt-1 text-[13.5px] font-semibold text-[#0a1020]/80">
            invece di <span className="line-through">€ 179</span> — risparmi <strong>80€</strong>
          </p>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-[14px] text-slate-700 leading-relaxed">
            Diventa <strong className="text-[#0a1f55]">Consulente Digitale, Tecnologico e di Sostenibilità</strong> certificato:
            20 ore di formazione tracciata, esame finale in formato ministeriale e attestato ufficiale A.C.D.T.
          </p>

          <ul className="mt-4 space-y-2 text-[13px]">
            <Bullet icon={GraduationCap} text="20 moduli con verifica presenza e quiz" />
            <Bullet icon={Award} text="Attestato riconosciuto Legge 4/2013" />
            <Bullet icon={ShieldCheck} text="Registro Nazionale Agg. MIMIT incluso" />
            <Bullet icon={Clock} text="Tempo di studio flessibile, 100% online" />
          </ul>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-center">
            <a
              href="#pacchetti"
              onClick={handleCta}
              data-testid="exit-popup-cta"
              className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-xl bg-[#0a1f55] hover:bg-[#102a6b] text-white font-extrabold text-[14px] transition-all shadow-lg hover:shadow-xl"
            >
              Approfitta ora
              <ChevronRight className="h-4 w-4" />
            </a>
            <button
              type="button"
              onClick={() => dismiss("later")}
              data-testid="exit-popup-later"
              className="text-[12px] text-slate-500 hover:text-slate-800 font-semibold text-center"
            >
              No grazie, forse più tardi
            </button>
          </div>

          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-center gap-4 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> Pagamento sicuro
            </span>
            <span>·</span>
            <span>Klarna · PayPal · Carta</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Bullet({ icon: Icon, text }) {
  return (
    <li className="flex items-start gap-2.5 text-slate-700">
      <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-[#f59e0b]/15 text-[#b45309] flex items-center justify-center">
        <Icon className="h-3 w-3" />
      </span>
      <span>{text}</span>
    </li>
  );
}
